import { useEffect, useState } from 'react'

// Shared "time until the event starts" logic, used by the guest invitation and
// by the dashboard event cards. Kept in one place so the two never disagree
// about when an event actually begins.

// Events are scheduled in Georgia's timezone (Asia/Tbilisi is a fixed UTC+4
// with no daylight saving). Anchoring to it means a host in another timezone
// still sees the countdown their guests see.
export const EVENT_TZ_OFFSET = '+04:00'

/** Epoch ms for an event's start, from the stored `YYYY-MM-DD` + `HH:MM`.
 *  Returns 0 when there is no date yet, which every consumer treats as "no
 *  countdown" rather than "the epoch". */
export const eventStartMs = (date?: string | null, time?: string | null): number => {
  if (!date) return 0
  const at = new Date(
    `${date}T${(time || '00:00').slice(0, 5)}:00${EVENT_TZ_OFFSET}`,
  ).getTime()
  return Number.isFinite(at) ? at : 0
}

export type CountdownValue = {
  d: string
  h: string
  m: string
  s: string
  /** True once the target has arrived (or there is no target at all). */
  done: boolean
}

/** Live countdown to `target` (epoch ms); values update once a second.
 *  Clamped at zero — it never counts up past the event. */
export const useCountdown = (target: number): CountdownValue => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  let t = Math.max(0, (target || 0) - now)
  const d = Math.floor(t / 864e5)
  t -= d * 864e5
  const h = Math.floor(t / 36e5)
  t -= h * 36e5
  const m = Math.floor(t / 6e4)
  t -= m * 6e4
  const s = Math.floor(t / 1e3)
  return {
    d: String(d),
    h: String(h).padStart(2, '0'),
    m: String(m).padStart(2, '0'),
    s: String(s).padStart(2, '0'),
    done: !target || target <= now,
  }
}

export const countdownLabels = (lang: string) =>
  lang === 'ka'
    ? { d: 'დღე', h: 'საათი', m: 'წუთი', s: 'წამი' }
    : { d: 'Days', h: 'Hrs', m: 'Min', s: 'Sec' }
